import * as cheerio from 'cheerio';

import type {
  TDocDiffRuntime,
  TReferenceData,
  TBlockInfo,
  TVersionedPair,
  THeadingInfo,
  TContentHooks,
} from '../doc-engine';
import type { TDocument, TDraft, THeading, TRefData, TRefItemType, TVersion } from '../types';
import {
  buildCaptionsMapFe,
  getCaptionPrefix,
  getResolveCaptionLabelFe,
  wrapImagesForAlign,
} from '@shared/backendFrontend';
import { getVersionId } from '@modules/items';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';
import { OutRefBookmarkKind } from '../tiptap/nodes/caption/constants';
import { collectLiteratureCitationIndices } from './literatureCitationIndices.helper';
import { TERMINOLOGY } from '@shared/i18n';
import { replaceImages, renderLatex } from './changesHtmlUtils';
import type { TMap } from '@shared/types';
import type { useGetBlob } from '../api';

/** Only what the runtime calls, so a caller can hand it a blob source that reads a cache first. */
export type TGetBlob = Pick<ReturnType<typeof useGetBlob>, 'mutateAsync'>;
export type TVersionHeadingsMap = TMap<string, THeading[]>;

export type TFrontendRuntimeSide = {
  version: TDraft | TVersion;
  headings: THeading[];
  captions: ReturnType<typeof buildCaptionsMapFe>;
  literatureCitationIndices?: Map<string, number>;
  versionHeadingsMap: TVersionHeadingsMap;
};

export const createFrontendRuntimeSide = (
  doc: TDocument,
  version: TDraft | TVersion,
  html: string,
  headings: THeading[]
): TFrontendRuntimeSide => {
  const selectedVersionId = getVersionId(version);
  const versionHeadingsMap: TVersionHeadingsMap = {};
  if (doc.draft) versionHeadingsMap.draft = selectedVersionId === 'draft' ? headings : doc.draft.headings;
  for (const candidate of doc.versions) {
    versionHeadingsMap[candidate.id] = candidate.id === selectedVersionId ? headings : candidate.headings;
  }

  const $ = cheerio.load(html);
  return {
    version,
    headings,
    captions: buildCaptionsMapFe($),
    literatureCitationIndices: collectLiteratureCitationIndices($),
    versionHeadingsMap,
  };
};

// ---------------------------------------------------------------------------
// Content hooks for platform-specific post-processing
// ---------------------------------------------------------------------------

export const createFrontendContentHooks = (): TContentHooks => ({
  processImages: ($, $root) => wrapImagesForAlign($, $root),
  processLatex: (html) => renderLatex(html),
});

// ---------------------------------------------------------------------------
// Runtime factory
// ---------------------------------------------------------------------------

export const createFrontendRuntime = (
  doc: TDocument,
  current: TFrontendRuntimeSide,
  previous: TFrontendRuntimeSide | undefined,
  getBlob: TGetBlob,
  /** Supplied only where a block should be drawn with its authoring header - see `renderBlockHeader`. */
  renderBlockHeader?: TDocDiffRuntime['renderBlockHeader']
): TDocDiffRuntime => {
  // Precompute heading maps for O(1) lookups
  const currentHeadingMap = new Map(current.headings.map((heading) => [heading.id, heading]));
  const previousHeadingMap = new Map(previous?.headings.map((heading) => [heading.id, heading]) ?? []);

  // Precompute label resolvers
  const resolveLabelCurr = getResolveCaptionLabelFe(current.version);
  const resolveLabelPrev = getResolveCaptionLabelFe(previous?.version);

  const runtime: TDocDiffRuntime = {
    renderBlockHeader,
    resolveLiteratureCitationIndex(literatureId: string): number | null {
      return current.literatureCitationIndices?.get(literatureId) ?? null;
    },
    resolveHeading(headingId: string): TVersionedPair<THeadingInfo> {
      const curr = currentHeadingMap.get(headingId);
      const prev = previousHeadingMap.get(headingId);
      return {
        current: curr ? { numbering: curr.numbering, level: curr.level } : undefined,
        previous: prev ? { numbering: prev.numbering, level: prev.level } : undefined,
      };
    },

    resolveCaptionPrefix(bookmarkKind: string, index: string, side: 'current' | 'previous'): string {
      const resolveLabel = side === 'current' ? resolveLabelCurr : resolveLabelPrev;
      return `${resolveLabel(bookmarkKind as OutRefBookmarkKind)} ${index}:`;
    },

    resolveReference(
      attrs: Readonly<Record<string, string>>,
      side: 'current' | 'previous'
    ): TReferenceData | undefined {
      const refId = attrs['data-id'];
      const itemType = attrs['data-item-type'];
      const versionMode = attrs['data-version-mode'];
      const headingId = attrs['data-heading-id'] ?? '';
      const bookmarkId = attrs['data-bookmark-id'];

      if (!refId || !itemType || !versionMode) return undefined;

      const selectedSide = side === 'current' ? current : previous;
      if (!selectedSide) return undefined;
      const resolveLabel = side === 'current' ? resolveLabelCurr : resolveLabelPrev;

      // Bookmark refs
      if (bookmarkId) {
        const captionEntry = selectedSide.captions[bookmarkId];
        if (!captionEntry) {
          return {
            bookmarkOriginalText: attrs['data-original-text'] || `Unknown ${TERMINOLOGY.reference.lower}`,
            itemName: '',
            itemActive: true,
          };
        }
        return {
          bookmarkPrefix: getCaptionPrefix(captionEntry, resolveLabel),
          itemName: '',
          itemActive: true,
        };
      }

      const isSelf = versionMode === 'SELF';
      let refData: TRefData;

      if (isSelf) {
        refData = { itemName: doc.name, itemActive: true };

        const selectedVersionId = (selectedSide.version as TVersion)?.id || 'draft';
        // Iteration order matters: keys are inserted in chronological order
        // (draft first, then newest → oldest) so the fallback finds the next older version.
        let foundVersion = false;
        for (const versionId of Object.keys(selectedSide.versionHeadingsMap)) {
          if (versionId === selectedVersionId) {
            foundVersion = true;
            const heading = selectedSide.versionHeadingsMap[versionId].find((h) => h.id === headingId);
            if (heading) {
              refData.headingData = { text: heading.text, numbering: heading.numbering };
              break;
            }
          } else if (foundVersion) {
            const heading = selectedSide.versionHeadingsMap[versionId].find((h) => h.id === headingId);
            if (heading) {
              refData.headingData = {
                text: heading.text,
                numbering: heading.numbering,
                fallbackVersionId: versionId,
              };
              break;
            }
          }
        }
      } else {
        refData = doc.refsData[refId];
      }

      if (!refData) return undefined;

      // Build TReferenceData from TRefData
      const itemName = getReferenceItemLabel(refData.itemName);

      return {
        itemName,
        itemActive: refData.itemActive,
        versionId: refData.versionId,
        itemId: isSelf ? undefined : attrs['data-item'],
        itemPath: isSelf ? undefined : getItemPathByItemType(itemType as TRefItemType),
        headingData: refData.headingData
          ? {
              text: refData.headingData.text,
              numbering: refData.headingData.numbering,
              fallbackVersionId: refData.headingData.fallbackVersionId,
            }
          : undefined,
      };
    },

    async getBlockInfo(
      blockId: string,
      attrs: Readonly<Record<string, string>>,
      side: 'current' | 'previous'
    ): Promise<TBlockInfo | undefined> {
      const version = side === 'current' ? current.version : previous?.version;
      if (!version) return undefined;
      const versionId = getVersionId(version);
      const blockData = doc.blocksData[blockId || ''];

      if (!blockData) {
        console.error(`Cannot load ${TERMINOLOGY.blockEither.lower} ${blockId} for doc ${doc._id}/${versionId}!`);
        return undefined;
      }

      const blockVersionEntry = blockData.versions[versionId];
      if (!blockVersionEntry) {
        console.error(
          `Cannot load ${TERMINOLOGY.blockEither.lower} ${blockId} for doc ${doc._id}/${versionId} for changes mode!`
        );
        return undefined;
      }

      if (blockData.itemType === 'INLINE_BLOCK') {
        return {
          html: blockVersionEntry?.variantFolderId ? (blockVersionEntry.value ?? '') : '',
          versionId: blockVersionEntry.blockVersion,
        };
      }

      const blobName = blockVersionEntry?.variantFolderId ? blockVersionEntry.blobName : '';
      const containerName = attrs['data-item'];

      try {
        let blockHtml = '';
        if (blobName) {
          blockHtml = await getBlob.mutateAsync({
            token: blockData.token,
            containerName,
            blobName,
          });
          blockHtml = replaceImages(containerName, blockData.token, blockHtml);
        }
        return {
          html: blockHtml,
          versionId: blockVersionEntry.blockVersion,
        };
      } catch (e) {
        console.error(
          `Failed to load ${TERMINOLOGY.block.lower} with blob-name ${blobName} for doc ${doc._id}/${versionId} for changes mode!`,
          e
        );
        return undefined;
      }
    },
  };

  return runtime;
};
